/**
 * 应用入口：Hash 路由、索引加载、检索页与阅读页
 */
import "./styles.css";
import MiniSearch from "minisearch";
import { assetUrl } from "./assetUrl.js";
import { appendHighlighted } from "./highlight.js";
import { miniSearchOptions } from "./miniSearchOptions.js";
import { extractPassage } from "./passage.js";
import { makeSnippet } from "./snippet.js";

const MAX_QUERY_LEN = 200;
const MAX_LIST = 80;

type Route =
  | { name: "search"; q: string }
  | { name: "read"; file: string; anchorId: string; q: string; juan: string };

type Doc = {
  id: string;
  anchorId: string;
  file: string;
  juan: number;
  text: string;
};

let miniSearchInstance: MiniSearch<Doc> | null = null;
let indexState: "idle" | "loading" | "ready" | "error" = "idle";

function parseHash(): Route {
  const h = window.location.hash.replace(/^#/, "") || "/search";
  const [path, queryString = ""] = h.split("?");
  const params = new URLSearchParams(queryString);

  if (path === "/read" || path.startsWith("/read")) {
    return {
      name: "read",
      file: params.get("file") ?? "",
      anchorId: params.get("anchor") ?? "",
      q: params.get("q") ?? "",
      juan: params.get("juan") ?? "",
    };
  }

  return {
    name: "search",
    q: params.get("q") ?? "",
  };
}

function navTo(route: Route): void {
  if (route.name === "search") {
    const qs = route.q ? `?q=${encodeURIComponent(route.q)}` : "";
    window.location.hash = `#/search${qs}`;
  } else {
    const qs = new URLSearchParams({
      file: route.file,
      anchor: route.anchorId,
      q: route.q,
      juan: route.juan,
    }).toString();
    window.location.hash = `#/read?${qs}`;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Partial<HTMLElementTagNameMap[K]> & { class?: string; text?: string },
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props) {
    const { class: cls, text, ...rest } = props;
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    Object.assign(node, rest);
  }
  for (const c of children) {
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

async function loadIndex(): Promise<void> {
  if (indexState === "ready" && miniSearchInstance) return;
  if (indexState === "loading") return;

  indexState = "loading";
  renderShell();

  try {
    const url = assetUrl("search-index.json");
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const parsed = (await res.json()) as { miniSearch: Record<string, unknown> };
    miniSearchInstance = MiniSearch.loadJS(
      parsed.miniSearch as Parameters<typeof MiniSearch.loadJS>[0],
      miniSearchOptions,
    );
    indexState = "ready";
  } catch {
    miniSearchInstance = null;
    indexState = "error";
  }

  renderShell();
}

function renderShell(): void {
  const app = document.getElementById("app");
  if (!app) return;

  app.textContent = "";
  const header = el("header", { class: "site-header" }, [
    el("h1", { class: "site-title", text: "明史全文检索" }),
    el("nav", { class: "site-nav" }, [
      el("a", { class: "nav-link", href: "#/search" }, ["检索"]),
      " · ",
      el("span", { class: "nav-hint", text: "Kanripo KR2a0038（四庫本系）" }),
    ]),
  ]);

  const main = el("main", { class: "site-main" });
  app.appendChild(header);
  app.appendChild(main);

  if (indexState === "loading") {
    main.appendChild(
      el("section", { class: "panel status-panel" }, [
        el("p", { class: "status-msg", text: "正在加载索引，请稍候…" }),
        el("p", {
          class: "status-hint",
          text: "首次加载约数十 MB，可能占用较多内存。",
        }),
      ]),
    );
    return;
  }

  if (indexState === "error") {
    const retry = el("button", {
      type: "button",
      class: "btn primary",
      text: "重试",
    }) as HTMLButtonElement;
    retry.addEventListener("click", () => {
      indexState = "idle";
      void loadIndex();
    });
    main.appendChild(
      el("section", { class: "panel status-panel error" }, [
        el("p", { class: "status-msg", text: "索引加载失败" }),
        el("p", {
          class: "status-hint",
          text: "请确认已执行 npm run build:index，并刷新页面重试。",
        }),
        retry,
      ]),
    );
    return;
  }

  if (indexState === "ready" && miniSearchInstance) {
    const route = parseHash();
    if (route.name === "read") {
      renderRead(main, route);
    } else {
      renderSearch(main, route);
    }
  }
}

function renderSearch(main: HTMLElement, route: Route & { name: "search" }): void {
  const ms = miniSearchInstance!;

  const form = el("form", { class: "search-form" });
  const input = el("input", {
    class: "search-input",
    type: "search",
    name: "q",
    placeholder: "输入连续字符串，如：太祖、張居正",
    value: route.q,
    maxLength: MAX_QUERY_LEN,
    autocomplete: "off",
  }) as HTMLInputElement;

  const submit = el("button", {
    type: "submit",
    class: "btn primary",
    text: "检索",
  });

  form.appendChild(input);
  form.appendChild(submit);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const q = input.value.trim();
    if (q.length > MAX_QUERY_LEN) {
      window.alert(`查询最长 ${MAX_QUERY_LEN} 字`);
      return;
    }
    navTo({ name: "search", q });
  });

  const panel = el("section", { class: "panel" }, [form]);

  if (route.q.trim()) {
    const q = route.q.trim();
    const rawHits = ms.search(q, { prefix: true, fuzzy: false });
    const hits = rawHits as unknown as Array<Doc & { score: number }>;
    hits.sort((a, b) => b.score - a.score);

    if (hits.length === 0) {
      panel.appendChild(
        el("div", { class: "empty-results", text: "无匹配结果，可尝试缩短或更换关键词。" }),
      );
    } else {
      const meta = el("p", {
        class: "result-meta",
        text: `共 ${hits.length} 条，以下显示前 ${Math.min(MAX_LIST, hits.length)} 条（按相关度排序）`,
      });
      panel.appendChild(meta);

      const list = el("ol", { class: "result-list" });
      for (const h of hits.slice(0, MAX_LIST)) {
        const snip = makeSnippet(h.text, q);
        const li = el("li", { class: "result-item" });
        const link = el("a", {
          class: "result-title",
          href: `#/read?${new URLSearchParams({
            file: h.file,
            anchor: h.anchorId,
            q,
            juan: String(h.juan),
          }).toString()}`,
        });
        link.textContent = `第 ${h.juan} 卷 · ${h.file.replace(/^KR2a0038\//, "")}`;
        const sub = el("div", { class: "result-sub", text: h.anchorId });
        const pre = el("pre", { class: "result-snippet" });
        pre.textContent = snip;
        li.appendChild(link);
        li.appendChild(sub);
        li.appendChild(pre);
        list.appendChild(li);
      }
      panel.appendChild(list);
    }
  }

  main.appendChild(panel);
}

function renderRead(main: HTMLElement, route: Route & { name: "read" }): void {
  const { file, anchorId, q, juan } = route;

  const crumb = el("nav", { class: "breadcrumb" }, [
    el("a", { href: "#/search", text: "检索" }),
    " › ",
    el("span", { text: file ? "…" : "（未指定文件）" }),
  ]);

  if (!file || !anchorId) {
    main.appendChild(
      el("section", { class: "panel error" }, [
        crumb,
        el("p", { text: "缺少 file 或 anchor 参数。" }),
        el("a", { class: "btn link", href: "#/search" }, ["返回检索"]),
      ]),
    );
    return;
  }

  const juanMatch = file.match(/KR2a0038_(\d+)\.txt$/);
  const juanLabel = juan
    ? `第 ${juan} 卷`
    : juanMatch
      ? `第 ${Number(juanMatch[1])} 卷`
      : file;
  crumb.textContent = "";
  crumb.appendChild(el("a", { href: "#/search", text: "检索" }));
  crumb.appendChild(document.createTextNode(" › "));
  crumb.appendChild(el("span", { text: `${juanLabel} · ${file}` }));

  const article = el("article", { class: "read-article" });
  article.appendChild(crumb);
  const status = el("p", { class: "read-status", text: "正在加载正文…" });
  const prose = el("div", { class: "read-prose mono" });
  article.appendChild(status);
  article.appendChild(prose);
  main.appendChild(article);

  const corpusUrl = assetUrl(file);
  fetch(corpusUrl)
    .then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.text();
    })
    .then((full) => {
      status.textContent = "";
      const passage = extractPassage(full, anchorId);
      appendHighlighted(prose, passage, q);
      window.requestAnimationFrame(() => {
        const first = prose.querySelector(".hit");
        first?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    })
    .catch(() => {
      status.textContent = "";
      prose.appendChild(
        el("p", {
          class: "error",
          text: "正文加载失败：请确认部署目录包含 KR2a0038 语料，或返回开发环境后重试。",
        }),
      );
      const back = el("a", { class: "btn link", href: "#/search" }, ["返回检索"]);
      prose.appendChild(back);
    });
}

window.addEventListener("hashchange", () => {
  if (indexState === "ready") renderShell();
});

void loadIndex();
